import './styles/StepWizard.scss';

import { AnimatePresence, motion, MotionStyle } from 'motion/react';
import {
  Children,
  createElement,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useClassHelper } from '../../hooks';
import { WizardContext } from './context';
import { configs, variants, WizardProps } from './types';

const componentName = 'c-stepWizard';

//**
// StepWizard/StepWizard.tsx
// This component is fundamental part for websheet Careful with the changes
//  */
const StepWizardComponent = ({
  children,
  startIndex = 0,
  withAnimation = true,
  ease = 'easeInOut',
  header,
  showTopIndicators = false,
  showBottomIndicators = false,
  hideIndicatorIndex,
  hideHeaderIndex,
  variant,
  config,
}: WizardProps) => {
  const stepCount = Children.toArray(children).length;
  const [activeStep, setActiveStep] = useState<number>(startIndex);
  const c = useClassHelper(componentName);

  const hasNextStep = activeStep < stepCount - 1;
  const hasPrevStep = activeStep > 0;

  const activeStepContent = useMemo(() => {
    const reactChildren = Children.toArray(children);

    return reactChildren[activeStep];
  }, [activeStep, children]);

  const goToStep = useCallback((stepIndex: number) => {
    setActiveStep(stepIndex);
  }, []);

  const nextStep = useCallback(() => {
    if (hasNextStep) {
      setActiveStep(prev => prev + 1);
    }
  }, [hasNextStep]);

  const prevStep = useCallback(() => {
    if (hasPrevStep) {
      setActiveStep(prev => prev - 1);
    }
  }, [hasPrevStep]);

  const reactChildren = useMemo(() => Children.toArray(children), [children]);

  const wizardValue = useMemo(
    () => ({
      activeStep,
      stepCount,
      nextStep,
      prevStep,
      hasNextStep,
      hasPrevStep,
      isFirstStep: activeStep === 0,
      isLastStep: activeStep === stepCount - 1,
      goToStep,
      currentStep: activeStep,
      steps: reactChildren,
    }),
    [
      activeStep,
      stepCount,
      hasNextStep,
      hasPrevStep,
      nextStep,
      prevStep,
      goToStep,
      reactChildren,
    ],
  );

  const renderIndicators = (tag = 'div') => {
    return !hideHeaderIndex?.includes(activeStep)
      ? createElement(
          tag,
          {
            className: c('__indicators', {
              '--top-indicator': tag !== 'footer',
            }),
          },
          <>
            {Array.from({ length: stepCount }).map((_, index) => {
              if (hideIndicatorIndex?.includes(index)) {
                return null;
              }

              return (
                <motion.button
                  type="button"
                  key={index}
                  className={c('__indicator-item', {
                    '--active': index === activeStep,
                  })}
                  onClick={() => goToStep(index)}
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{
                    scale: index === activeStep ? 1.2 : 1,
                    opacity: index === activeStep ? 1 : 0.5,
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 20,
                  }}
                />
              );
            })}
          </>,
        )
      : null;
  };

  const styles: MotionStyle =
    variant === variants.fullWidth
      ? {}
      : {
          flex: 1,
          display: 'flex',
          position: 'relative',
        };

  const isFullHeightStyle =
    config === configs.fullHeight ? { height: '100%' } : {};

  return (
    <WizardContext.Provider value={wizardValue}>
      <div className={componentName} style={isFullHeightStyle}>
        {header && !hideHeaderIndex?.includes(activeStep) && (
          <header className={c('__header')}>
            <span>{header}</span>
            {showTopIndicators && renderIndicators()}
          </header>
        )}
        <AnimatePresence key={activeStep}>
          <motion.div
            key={activeStep}
            initial={withAnimation ? { opacity: 0, y: 15 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.2,
              ease,
            }}
            style={{ ...styles, ...isFullHeightStyle }}
          >
            {activeStepContent}
          </motion.div>
        </AnimatePresence>
        {showBottomIndicators && renderIndicators('footer')}
      </div>
    </WizardContext.Provider>
  );
};

const Item = ({ children }: { children: React.ReactNode }) => {
  return <div className="table-row">{children}</div>;
};

type StepWizardType = typeof StepWizardComponent & {
  Item: typeof Item;
};

const StepWizard = memo(StepWizardComponent) as unknown as StepWizardType;

StepWizard.Item = Item;

export { StepWizard };
